/*
  FD brand: the Soft sit upgrade guard.

  Soft sit is the supplier's medium foam and is charged for with a separate
  product line, added alongside the sofa by fd-addons.js. Shopify has no way to
  bind two basket lines together on this plan -- Functions need Plus -- so a
  shopper can delete the upgrade line and keep the upgraded sofa. This file
  puts it back.

  The basket describes its own arithmetic, which is the whole trick:
  - every SOFA line bought with Soft sit carries `_foam_variant`, the id of the
    upgrade variant it owes one unit of;
  - every UPGRADE line carries `_foam_upgrade`, so a stale one is still
    identifiable after the sofa that paid for it has gone.

  So the guard never needs to be told today's prices, today's tiers, or which
  product is the upgrade. It reads /cart.js, counts, and corrects. Underscore-
  prefixed properties are hidden in both cart renders, so none of this is
  visible to the shopper.

  It FAILS OPEN, deliberately and consistently with the delivery rules: if any
  request fails the basket is left exactly as the shopper left it, the failure
  is logged, and the next basket update tries again. A guard that could empty
  or block a basket would be worse than the leak it closes.

  The load-time sync is Liquid-gated to baskets that already carry a foam
  property (`data-fd-foam-check` on <body>); the pub/sub path is not gated.
*/
(function () {
  'use strict';

  var SOURCE = 'fd-foam-guard';
  // fd-addons.js writes a timestamp here just before it adds an upgrade line
  // and clears it once the sofa add that follows has settled. Shared with
  // that file by name; a marker older than PENDING_TTL is ignored.
  var PENDING = 'FdFoamAddPending';
  var PENDING_TTL = 10000;
  var RETRY_MS = 400;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function json(url, body) {
    var options = { headers: { Accept: 'application/json' } };
    if (body) {
      options.method = 'POST';
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }
    return fetch(url, options).then(function (response) {
      if (!response.ok) throw new Error(url + ' returned HTTP ' + response.status);
      return response.json();
    });
  }

  function propertyOf(item, name) {
    return item && item.properties ? item.properties[name] : null;
  }

  /*
    The visible label a downgraded sofa line falls back to. The comfort picker
    stamps it on the line at add time (`_foam_property` = the block's property
    name, `_foam_firm` = its Firm option label), so this file never has to know
    -- or invent -- a customer-facing word. A line without both stamps (a
    basket from before 2026-10-06) cannot be downgraded honestly, so it keeps
    the old behaviour: the upgrade it owes is put back.
  */
  function firmLabel(item) {
    var name = propertyOf(item, '_foam_property');
    var label = propertyOf(item, '_foam_firm');
    if (!name || !label) return null;
    return { name: name, label: label };
  }

  function downgraded(item, firm) {
    var out = {};
    Object.keys(item.properties || {}).forEach(function (name) {
      if (name.indexOf('_foam_') === 0) return;
      out[name] = item.properties[name];
    });
    out[firm.name] = firm.label;
    return out;
  }

  /*
    The smallest set of writes that makes the basket honest. Pure: it reads a
    /cart.js payload and returns writes, so it is unit-tested on its own
    (scripts/theme-js/fd-foam-guard.test.js).

    Per upgrade variant the sofas owe:
    - an upgrade line is present but the count is off  -> set its quantity (the
      sofa quantity went up or down; the upgrade follows it);
    - NO upgrade line at all                           -> the shopper deleted it
      (user ruling 2026-10-06), so each sofa line owing it is rewritten to the
      Firm option and loses its `_foam_*` properties. Nothing legitimate leaves
      that state: fd-addons.js adds the upgrade BEFORE the sofa and publishes
      nothing in between, and if that add failed the sofa honestly becomes
      Firm rather than staying Soft sit unpaid;
    - an upgrade line nobody owes                      -> removed (the sofa
      went).
    Removing a line is a change to quantity 0 rather than a delete, so the
    whole reconciliation speaks one endpoint's language.
  */
  function plan(cart) {
    var items = (cart && cart.items) || [];
    var want = {};
    var owing = {};
    var have = {};
    var out = [];

    items.forEach(function (item) {
      var id = propertyOf(item, '_foam_variant');
      if (id) {
        want[id] = (want[id] || 0) + item.quantity;
        (owing[id] = owing[id] || []).push(item);
      }
      if (propertyOf(item, '_foam_upgrade')) {
        (have[item.variant_id] = have[item.variant_id] || []).push(item);
      }
    });

    Object.keys(want).forEach(function (id) {
      var lines = have[id];
      if (lines) {
        if (lines[0].quantity !== want[id]) out.push({ key: lines[0].key, quantity: want[id] });
        return;
      }
      var legacy = 0;
      owing[id].forEach(function (item) {
        var firm = firmLabel(item);
        if (!firm) {
          legacy += item.quantity;
          return;
        }
        out.push({ key: item.key, quantity: item.quantity, properties: downgraded(item, firm) });
      });
      if (legacy) out.push({ add: Number(id), quantity: legacy });
    });

    Object.keys(have).forEach(function (id) {
      have[id].forEach(function (line, index) {
        if (!want[id] || index > 0) out.push({ key: line.key, quantity: 0 });
      });
    });

    return out;
  }

  function applyOne(fix) {
    if (fix.add) {
      return json(root() + 'cart/add.js', {
        items: [{
          id: fix.add,
          quantity: fix.quantity,
          properties: { _foam_upgrade: '1' },
        }],
      });
    }
    var body = { id: fix.key, quantity: fix.quantity };
    // /cart/change.js REPLACES the whole property set when `properties` is
    // sent; plan() passes the full new set, every visible property kept.
    if (fix.properties) body.properties = fix.properties;
    return json(root() + 'cart/change.js', body);
  }

  /*
    Serial, not parallel: /cart/change.js keys are line item keys, and two
    concurrent writes against the same basket can invalidate each other's.
  */
  function applyAll(fixes) {
    return fixes.reduce(function (chain, fix) {
      return chain.then(function () { return applyOne(fix); });
    }, Promise.resolve());
  }

  function announce() {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    // The drawer and the cart page both re-render from this. The source name
    // is what stops the guard from answering its own broadcast.
    publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE });
  }

  function pass() {
    return json(root() + 'cart.js')
      .then(function (cart) {
        var fixes = plan(cart);
        if (!fixes.length) return false;
        return applyAll(fixes).then(function () { return true; });
      })
      .then(function (changed) {
        if (changed) announce();
      })
      .catch(function (error) {
        console.error('fd-foam-guard: the basket was left as it was', error);
      });
  }

  /*
    When a pass may run. Two rules, both from the W1 review (M1):
    - A call that arrives while a pass is in flight is not dropped: it marks
      the runner dirty and exactly ONE more pass runs afterwards, against a
      fresh /cart.js, however many calls arrived meanwhile.
    - While fd-addons.js is between its upgrade add and the sofa add, the
      basket briefly holds an upgrade that no sofa owes yet. A pass then would
      remove it as an orphan and the sofa would land owing it, i.e. be
      downgraded. So the runner WAITS (one retry timer, however many calls)
      until the marker clears -- and FAILS OPEN: a marker older than the ttl is
      ignored, so a stuck flag can never switch the guard off.
    Pure apart from what `env` injects, so it is unit-tested with fake timers.
  */
  function makeRunner(task, env) {
    var busy = false;
    var dirty = false;
    var waiting = false;

    function run() {
      if (busy) {
        dirty = true;
        return Promise.resolve();
      }
      var since = env.pendingSince();
      if (since && env.now() - since < env.ttl) {
        if (!waiting) {
          waiting = true;
          env.setTimeout(function () {
            waiting = false;
            run();
          }, env.retryMs);
        }
        return Promise.resolve();
      }
      busy = true;
      return Promise.resolve()
        .then(task)
        .catch(function () {})
        .then(function () {
          busy = false;
          if (dirty) {
            dirty = false;
            return run();
          }
        });
    }

    return run;
  }

  var sync = makeRunner(pass, {
    now: function () { return Date.now(); },
    setTimeout: function (fn, ms) { return window.setTimeout(fn, ms); },
    pendingSince: function () { return Number(window[PENDING]) || 0; },
    ttl: PENDING_TTL,
    retryMs: RETRY_MS,
  });

  /*
    The load-time pass is Liquid-gated (theme.liquid writes
    data-fd-foam-check="1" on <body> only when the basket already carries a
    `_foam_variant` or `_foam_upgrade` property), so a basket with nothing to
    reconcile costs no /cart.js request. The pub/sub path below stays
    unconditional: it is what catches the deletion that creates the mismatch in
    the first place, and the flag on <body> is a snapshot of the page render.
  */
  // Exposed for the unit tests and for debugging; nothing on the page calls it.
  window.FdFoamGuard = { plan: plan, makeRunner: makeRunner };

  document.addEventListener('DOMContentLoaded', function () {
    if (document.body.dataset.fdFoamCheck !== '1') return;
    sync();
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.source === SOURCE) return;
      return sync();
    });
  }
})();
